/**
 * Stream subscription management
 * Handles stream processor subscription and message updates
 */

import { useEffect, useRef } from 'react';
import type { ThreadMessage } from '@/types/messages';
import type { LLMEventStreamProcessor } from '@/services/LLMEventStreamProcessor';
import { logger } from '@/utils/logger';

interface UseStreamSubscriptionProps {
  streamProcessor: LLMEventStreamProcessor;
  currentThreadId: string | null;
  setAvailableIds: (
    ids: Set<string> | ((prev: Set<string>) => Set<string>)
  ) => void;
  onMessageUpdate?: (messages: ThreadMessage[]) => void;
}

export function useStreamSubscription({
  streamProcessor,
  currentThreadId,
  setAvailableIds,
  onMessageUpdate,
}: UseStreamSubscriptionProps) {
  // Refs so the subscriber closure always reads the latest values without
  // being listed as useEffect dependencies.  Listing `messages` or
  // `onMessageUpdate` as deps would cause the effect to unsubscribe and
  // re-subscribe on every SSE event (messages changes every chunk), which
  // creates ~10-50 subscribe/unsubscribe cycles per second mid-stream and
  // is the primary driver of the "message disappears" bug.
  const currentThreadIdRef = useRef(currentThreadId);
  const onMessageUpdateRef = useRef(onMessageUpdate);

  currentThreadIdRef.current = currentThreadId;
  onMessageUpdateRef.current = onMessageUpdate;

  useEffect(() => {
    const unsubscribe = streamProcessor.subscribe((newMessages) => {
      try {
        const threadId = currentThreadIdRef.current;
        if (!threadId || newMessages.length === 0) return;

        setAvailableIds((prevIds) => {
          const newAvailableIds = new Set(prevIds);
          newMessages.forEach((msg) => {
            if (
              msg.metadata?.availableIds &&
              Array.isArray(msg.metadata.availableIds)
            ) {
              (msg.metadata.availableIds as string[]).forEach((id) =>
                newAvailableIds.add(id)
              );
            }
          });
          return newAvailableIds;
        });

        if (onMessageUpdateRef.current) {
          // Pass only messages for the current thread to the update callback.
          // We read from the stream processor's source of truth rather than
          // from the React state snapshot to avoid stale closures.
          const currentMessages = newMessages.filter(
            (msg) => msg.threadId === threadId
          );
          if (currentMessages.length > 0) {
            onMessageUpdateRef.current(currentMessages);
          }
        }
      } catch (error) {
        logger.error('Error processing stream subscription:', error);
      }
    });

    return () => {
      unsubscribe();
    };
    // Intentionally exclude messages, currentThreadId, onMessageUpdate:
    // they are read via refs to prevent teardown/re-subscribe on every
    // streaming event. setAvailableIds is a stable useState setter.
  }, [streamProcessor, setAvailableIds]);
}